import { useState, useCallback, useEffect } from "react";
import html2canvas from "html2canvas";

let jsPDFInstance: any = null;
const loadJsPdf = async () => {
  try {
    if (!jsPDFInstance) {
      const { jsPDF } = await import("jspdf");
      jsPDFInstance = jsPDF;
    }
  } catch (error) {
    console.error("Failed to load jsPDF:", error);
    return false;
  }
  return true;
};

type ExtendedWindow = {
  isFlutterApp?: string;
  flutter_inappwebview?: {
    callHandler?: (name: string, data?: any) => void;
  };
};

export const useDownloadPDF = () => {
  const [processingPDF, setProcessingPDF] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      loadJsPdf();
    }
  }, []);

  const downloadPDF = useCallback(async (content: string, pdfName: string) => {
    if (typeof content !== "string" || typeof pdfName !== "string")
      throw new Error(
        "Invalid input: content must be HTML string and pdfName must be a string."
      );

    try {
      setProcessingPDF(true);

      const tempDiv = document.createElement("div");
      tempDiv.style.position = "absolute";
      tempDiv.style.top = "-9999px";
      tempDiv.style.width = "768px"; // Set width to match expected dimensions
      tempDiv.innerHTML = content;
      document.body.appendChild(tempDiv);

      // Capture the content as a canvas
      const fullCanvas = await html2canvas(tempDiv, {
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      // Define A4 page dimensions (in points) and margins
      const A4_WIDTH = 595.28;
      const A4_HEIGHT = 841.89;
      const MARGIN = 10;
      const CONTENT_WIDTH = A4_WIDTH - 2 * MARGIN;
      const CONTENT_HEIGHT = A4_HEIGHT - 2 * MARGIN;

      const canvasWidth = fullCanvas.width;
      const canvasHeight = fullCanvas.height;

      const scaleRatio = CONTENT_WIDTH / canvasWidth;
      const totalPages = Math.ceil(
        (canvasHeight * scaleRatio) / CONTENT_HEIGHT
      );

      const canvases: HTMLCanvasElement[] = [];

      // Split the created canvas into multiple pages if required
      for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        const isLastPage = pageIndex === totalPages - 1;
        const remainingHeight =
          canvasHeight - pageIndex * (CONTENT_HEIGHT / scaleRatio);
        const actualContentHeight = isLastPage
          ? remainingHeight * scaleRatio
          : CONTENT_HEIGHT;

        const newCanvas = document.createElement("canvas");
        // Multiply by 2 to increase DPI
        newCanvas.width = CONTENT_WIDTH * 2;
        newCanvas.height = actualContentHeight * 2;

        const context = newCanvas.getContext("2d");
        if (!context) {
          throw new Error("Failed to get canvas context");
        }

        const sourceY = pageIndex * (CONTENT_HEIGHT / scaleRatio);

        context.drawImage(
          fullCanvas,
          0,
          sourceY,
          canvasWidth,
          actualContentHeight / scaleRatio,
          0,
          0,
          newCanvas.width,
          newCanvas.height
        );

        canvases.push(newCanvas);
      }

      await loadJsPdf();

      // Now iterate through the canvases array and add them to jsPDF doc.
      const doc = new jsPDFInstance("p", "pt", "a4");
      canvases.forEach((canvas, index) => {
        if (index > 0) doc.addPage();
        const imgData = canvas.toDataURL("image/png");
        doc.addImage(
          imgData,
          "PNG",
          MARGIN,
          MARGIN,
          CONTENT_WIDTH,
          canvas.height / 2 // Canvas height is divided by 2 as it was multiplied by 2 earlier to increase DPI.
        );
      });
      // Clean up
      document.body.removeChild(tempDiv);

      // Handle Flutter app or download the PDF
      const windowTyped = window as unknown as ExtendedWindow;
      if (
        windowTyped.isFlutterApp === "true" &&
        windowTyped.flutter_inappwebview?.callHandler
      ) {
        const pdfDataUri = doc.output("datauristring");
        windowTyped.flutter_inappwebview.callHandler("downloadPdf", pdfDataUri);
      } else {
        doc.save(`${pdfName}.pdf`);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setProcessingPDF(false);
    }
  }, []);

  return { downloadPDF, processingPDF };
};
